import React, { useEffect, useRef, useState } from 'react';

import CategoryTile, { CategoryTileProps } from './CategoryTile';
import {
  SectionWrapper,
  TilesWrapper,
  StyledH3,
  HedWrapper,
  HeadActions,
  ScrollContainer,
  LeftButton,
  RightButton,
  NoWrapLink,
  NoWrapButton,
} from './CategoryTiles.styles';
import { ChevronLeftIcon } from '../SvgIconsV3/ChevronLeft';
import { ChevronRightIcon } from '../SvgIconsV3/ChevronRight';

export type CategoryTilesProps = {
  title?: string;
  categories: CategoryTileProps[];
  cta?: {
    linkText: string;
    href: string;
    /* If true, will display the link the style of a ButtonV3 */
    displayAsButton?: boolean;
  };
  actions?: React.ReactNode;
  /* Displays a black overlay on the bottom section of the cards */
  showScrim?: boolean;
  /* Variant of the card with a square image and a link below the image  */
  square?: boolean;
  tileButtonTextTransform?: boolean;
};

const getGap = (element: HTMLDivElement) =>
  parseInt(window.getComputedStyle(element).getPropertyValue('column-gap'), 10);

// A generic scroller which can have different size of tiles

export type CategoryScrollerProps = {
  square?: boolean;
  children?: React.ReactNode;
};

/**
 * @deprecated components/PreAuthV3/CategoryScroller is deprecated, use components/TileScroller
 */

export const CategoryScroller: React.FC<CategoryScrollerProps> = (props) => {
  const { children, square } = props;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scrollable, setScrollable] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const childrenArray = Array.from(containerRef.current.children);
        const gap = getGap(containerRef.current);
        const totalGap = childrenArray.length > 1 ? gap * (childrenArray.length - 1) : 0;
        const childrenWidth = childrenArray.reduce((sum, child) => sum + child.clientWidth, 0);
        const totalContentWidth = totalGap + childrenWidth;
        setScrollable(totalContentWidth > containerRef.current.clientWidth);
      }
    };

    // Add event listener
    window.addEventListener('resize', handleResize);

    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [containerRef]);

  const scrollTo = (position: 'left' | 'right') => {
    if (!containerRef.current) return;

    const isLeft = position === 'left';
    const isRight = position === 'right';
    const gap = getGap(containerRef.current);

    let scrollLeft = 0;
    const containerWidth = containerRef.current.clientWidth;
    const currentScrollLeft = containerRef.current.scrollLeft;

    for (const child of Array.from(containerRef.current.children)) {
      if (isLeft && scrollLeft >= currentScrollLeft - containerWidth) break;
      if (isRight && scrollLeft + child.clientWidth >= containerWidth + currentScrollLeft) break;

      scrollLeft += child.clientWidth + gap;
    }

    containerRef.current.scroll({
      left: scrollLeft,
    });
  };

  return (
    <ScrollContainer scrollable={scrollable}>
      <LeftButton onClick={() => scrollTo('left')} square={square}>
        <ChevronLeftIcon width={20} height={20} />
      </LeftButton>
      <TilesWrapper ref={containerRef}>{children}</TilesWrapper>
      <RightButton onClick={() => scrollTo('right')} square={square}>
        <ChevronRightIcon width={20} height={20} />
      </RightButton>
    </ScrollContainer>
  );
};

const CategoryTiles: React.FC<CategoryTilesProps> = (props: CategoryTilesProps) => {
  const {
    categories,
    title,
    cta,
    actions,
    showScrim,
    square,
    tileButtonTextTransform = true,
  } = props;

  return (
    <SectionWrapper>
      <HedWrapper>
        {title && <StyledH3>{title}</StyledH3>}
        {cta &&
          (cta.displayAsButton ? (
            <NoWrapButton component="a" href={cta.href}>
              {cta.linkText}
            </NoWrapButton>
          ) : (
            <NoWrapLink arrow inline href={cta.href}>
              {cta.linkText}
            </NoWrapLink>
          ))}
        {actions ? <HeadActions>{actions}</HeadActions> : null}
      </HedWrapper>
      <CategoryScroller square={square}>
        {categories.map((category, index) => (
          <CategoryTile
            {...category}
            showScrim={showScrim}
            key={`${title}-category-${index}`}
            square={square}
            tileButtonTextTransform={tileButtonTextTransform}
          />
        ))}
      </CategoryScroller>
    </SectionWrapper>
  );
};

export default CategoryTiles;
